<template>
  <div class="form-group">
    <h3>Step {{ currentStep }}. Your payment details</h3>
    <div class="infomsg">Please add a payment method to use for your travel bookings and our booking fee. We process all payments securely using <a href="https://stripe.com" target="_blank">Stripe</a>.</div>

    <div class="form-group">
      <div class="form-row">
        <input v-model="card.name" type="text" required placeholder="Name on card" />
      </div>
      <div class="form-row">
        <input v-model="card.number" type="text" required placeholder="Card number" inputmode="numeric" pattern="[0-9]*" />
      </div>
      <div class="form-row">
        <div class="form-col">
          <select v-model="card.expiryMonth" required>
            <option value="" disabled>Expiry month</option>
            <option v-for="m in 12" :key="m" :value="m">
              {{ m < 10 ? "0" + m : m }}
            </option>
          </select>
        </div>
        <div class="form-col">
          <select v-model="card.expiryYear" required>
            <option value="" disabled>Expiry year</option>
            <option v-for="y in years" :key="y" :value="y">
              {{ y }}
            </option>
          </select>
        </div>
      </div>
      <div class="form-row">
        <div class="form-col">
          <input v-model="card.security" type="text" required placeholder="Security code (3 or 4 digits)" maxlength="4" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-col">
          <input v-model="card.addressStreet" type="text" required placeholder="Street address" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-col">
          <input v-model="card.addressCity" type="text" required placeholder="City" />
        </div>
        <div class="form-col">
          <input v-model="card.addressState" type="text" required placeholder="State" maxlength="2" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-col">
          <input v-model="card.addressZip" type="text" required placeholder="Zip" />
        </div>
        <div class="form-col">
          <select v-model="card.addressCountry" required>
            <option value="" disabled>Country</option>
            <option v-for="country in countries" :key="country.id" :value="country.title">
              {{ country.title }}
            </option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import useApi from "../utils/apiCalls.js";

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },
  currentStep: {
    type: Number,
    required: true,
  },
  modelValue: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["update:modelValue"]);

const { apiGetRequest } = useApi();
const countries = ref([]);

// Initialize card with modelValue or defaults
const card = reactive({
  name: props.modelValue?.name || null,
  number: props.modelValue?.number || null,
  expiryMonth: props.modelValue?.expiryMonth || "",
  expiryYear: props.modelValue?.expiryYear || "",
  security: props.modelValue?.security || null,
  addressStreet: props.modelValue?.addressStreet || null,
  addressCity: props.modelValue?.addressCity || null,
  addressState: props.modelValue?.addressState || null,
  addressZip: props.modelValue?.addressZip || null,
  addressCountry: props.modelValue?.addressCountry || "",
});

const years = computed(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i <= currentYear + 10; i++) {
    years.push(i);
  }
  return years;
});

// Sync from parent when modelValue is replaced (e.g. dev fill)
watch(
  () => props.modelValue,
  val => {
    if (val) {
      Object.assign(card, val);
    }
  }
);

// Emit card data when it changes
watch(
  card,
  newCard => {
    emit("update:modelValue", { ...newCard });
  },
  { deep: true, immediate: true }
);

onMounted(async () => {
  try {
    countries.value = (await apiGetRequest({ path: "/countries" })) || [];
  } catch (error) {
    console.error("Error loading countries:", error);
  }
});
</script>
